import { Button, Spacer, Text } from '@sparemin/blockhead';
import React from 'react';

import AutomationPostModalAdditionalDetails from 'components/AutomationPostModalAdditionalDetails';
import AutomationPostModalHeader from 'components/AutomationPostModalHeader';
import CaptionsLearnCenterLink from 'components/CaptionsLearnLink';
import InputFieldAsButton from 'components/Form/InputFieldAsButton';
import { EditThin } from 'components/icons';
import LoadingOverlay from 'components/LoadingOverlay';
import ModalContentWithMedia from 'components/Modal/ModalContentWithMedia';
import ShareFormTextarea from 'components/ShareForm/ShareFormTextarea';
import SocialAccountInfo, {
  SocialAccountInfoProps,
} from 'components/SocialAccountInfo';
import { Step } from 'components/SteppedModal';
import { IStep } from 'components/SteppedModal/types';
import { TikTokPrivacyLevelOptionName } from 'redux/middleware/api/third-party-authentication-service/types';

import { MAX_CHARS_CAPTIONS } from '../constants';
import TikTokLegalText from '../TikTokLegalText';
import TikTokVisibilitySelector from '../TikTokVisibilitySelector';
import { TikTokUserAllowance } from '../types';
import { block } from '../utils';
import { DEFAULT_POST_COMPOSER_STEP_DESCRIPTION } from './constants';
import UserAllowance from './UserAllowance';

type PickedSocialAccountProps = Pick<
  SocialAccountInfoProps,
  'username' | 'avatarSrc' | 'onSwitchAccountClick'
>;

export type UsePostComposerBaseConfig = Omit<
  PickedSocialAccountProps,
  'onInitialStepClick'
> &
  Pick<IStep, 'submitButtonLabel'>;

export interface UsePostComposerStepConfig extends UsePostComposerBaseConfig {
  description?: string;
  captions: string;
  privacyLevel: TikTokPrivacyLevelOptionName;
  userAllowance: TikTokUserAllowance[];
  brandedContentSelected: boolean;
  allowSubmit: boolean;
  captionCompositionMode?: 'freeForm' | 'withMetaData';
  isUploading?: boolean;
  onCaptionsClick?: () => void;
  onCaptionsChange?: (captions: string) => void;
  onPrivacyLevelChange?: (privacyLevel: TikTokPrivacyLevelOptionName) => void;
  onUserAllowanceChange?: (userAllowance: TikTokUserAllowance[]) => void;
  onAdditionalDetailsClick?: () => void;
  onHideModal?: () => void;
  onSubmit?: () => void;
}

export default function usePostComposerStep({
  username,
  avatarSrc,
  description = DEFAULT_POST_COMPOSER_STEP_DESCRIPTION,
  captions,
  privacyLevel,
  userAllowance,
  brandedContentSelected,
  allowSubmit,
  captionCompositionMode = 'withMetaData',
  isUploading,
  submitButtonLabel = 'confirm auto-posting details',
  onSwitchAccountClick,
  onCaptionsClick,
  onCaptionsChange,
  onPrivacyLevelChange,
  onUserAllowanceChange,
  onAdditionalDetailsClick,
  onHideModal,
  onSubmit,
}: UsePostComposerStepConfig): Step {
  return {
    id: 'composer',
    hideFooter: true,
    replaceBody: true,
    component: (
      <>
        <ModalContentWithMedia
          onClose={onHideModal}
          className="composer-step-content"
        >
          {isUploading && (
            <LoadingOverlay
              title="Posting Video"
              className={block('posting-loader')}
            />
          )}

          <ModalContentWithMedia.ModalContentWithMediaColumn>
            <img
              src="https://static.sparemin.com/static/webapp-assets/auto-posting-detail/auto-posting-detail-tik-tok-image-a-202403191010.png"
              className={block('composer-image-left')}
            />
          </ModalContentWithMedia.ModalContentWithMediaColumn>

          <ModalContentWithMedia.ModalContentWithMediaColumn className="composer-content-column">
            <Spacer
              orientation="vertical"
              align="flex-start"
              space="20px"
              className={block('composer-wrapper')}
            >
              <SocialAccountInfo
                {...{
                  avatarSrc,
                  onSwitchAccountClick,
                  username,
                }}
              />

              <AutomationPostModalHeader
                title="Let’s set up your video details"
                description={description}
              />

              {captionCompositionMode === 'withMetaData' ? (
                <InputFieldAsButton
                  label={
                    <Text variant="label">
                      <b>Example of your video descriptions</b>{' '}
                      <CaptionsLearnCenterLink />
                    </Text>
                  }
                  content={captions}
                  tooltipContent="Edit format of captions"
                  icon={EditThin}
                  onClick={onCaptionsClick}
                  className={block('composer-single-line')}
                />
              ) : (
                <ShareFormTextarea
                  hideProgress
                  type="textarea"
                  label="Captions"
                  placeholder="Enter your caption here"
                  maxChars={MAX_CHARS_CAPTIONS}
                  value={captions}
                  className={block('composer-wrapper')}
                  controlClassName={block('captions-free-form-input')}
                  onChange={onCaptionsChange}
                />
              )}

              <TikTokVisibilitySelector
                brandedContentSelected={brandedContentSelected}
                value={privacyLevel}
                onChange={onPrivacyLevelChange}
              />

              <UserAllowance {...{ userAllowance, onUserAllowanceChange }} />

              <AutomationPostModalAdditionalDetails
                description="Disclose promotional goods or services in your videos."
                onClick={onAdditionalDetailsClick}
              />
            </Spacer>

            <Spacer
              orientation="vertical"
              align="center"
              space="18px"
              className={block('fluid-content')}
            >
              <Button
                variant="solid"
                fluid
                className={block('composer-step-submit-button')}
                onPress={onSubmit}
                isDisabled={!allowSubmit}
              >
                {submitButtonLabel}
              </Button>

              <TikTokLegalText
                brandedContentSelected={brandedContentSelected}
              />
            </Spacer>
          </ModalContentWithMedia.ModalContentWithMediaColumn>
        </ModalContentWithMedia>
      </>
    ),
  };
}
